import { StepForm } from '@/components/StepForm';
import { BillSize, FaAnswers } from '@/state/state.types';
import { Button, Card, Group, Text, useMantineTheme } from '@mantine/core';
import { Dispatch, SetStateAction, useMemo } from 'react';
import { ArrowUpward } from '@/components/icons/ArrowUpward';
import { useFields } from '../hooks/useFields';
import { LegacyActions } from './LegacyActions';
import { LegacyHeader } from './LegacyHeader';
import { buildLegacyConfig } from './legacyConfig';

interface Props {
  isPartnershipPreset: boolean;
  partnership?: string;
  logoSrc?: string;
  firstName: string;
  lastName: string;
  isLoading: boolean;
  setAnswer: Dispatch<SetStateAction<'' | FaAnswers>>;
}

const FinancialAidStepLegacy = ({
  isPartnershipPreset,
  partnership,
  logoSrc,
  firstName,
  lastName,
  isLoading,
  setAnswer,
}: Props) => {
  const theme = useMantineTheme();
  const fields = useFields();
  const isLessThan5k = BillSize.Values['less-than-5k'] === fields?.['bill-size'];

  const config = useMemo(
    () => buildLegacyConfig({ isPartnershipPreset, partnership, firstName, lastName, theme }),
    [isPartnershipPreset, partnership, firstName, lastName, theme],
  );

  return (
    <StepForm>
      <Card
        bg={config.card.bg}
        radius={12}
        w="100%"
        p={26}
      >
        <LegacyHeader
          isPartnershipPreset={isPartnershipPreset}
          partnership={partnership}
          logoSrc={logoSrc}
          headerText={config.content.headerText}
          descriptionText={config.content.descriptionText}
        />
        <LegacyActions
          config={config}
          isPartnershipPreset={isPartnershipPreset}
          isLessThan5k={isLessThan5k}
          isLoading={isLoading}
          onPrimary={() => setAnswer(FaAnswers.yes)}
          onRejected={() => setAnswer(FaAnswers.rejected)}
        />
      </Card>

      {/* Bottom Navigation Button */}
      {!isLessThan5k && (
        <Button
          type="button"
          onClick={() => setAnswer(FaAnswers.no)}
          mt={20}
          ml={10}
          mr={10}
          loading={isLoading}
          sx={{
            root: {
              backgroundColor: 'transparent',
              border: 'none',
            },
          }}
          p={0}
        >
          <Group spacing={2}>
            <>
              <Text
                mr={4}
                fw={700}
                c="primary.0"
              >
                {config.content.bottomButtonText}
              </Text>
              <ArrowUpward color={theme.colors.tertiary[1]} />
            </>
          </Group>
        </Button>
      )}
    </StepForm>
  );
};

export default FinancialAidStepLegacy;
